'use client'
import { SbBlokData, storyblokEditable, StoryblokServerComponent } from '@storyblok/react/rsc'
import { FC, useEffect, useState } from 'react'

import { searchParamsData } from '@/lib/sbAPI'
import { getStoryblokApi } from '@/lib/storyblok'

import { PhnxAuthorcard } from '../Authorcard/Authorcard'
import { PhnxFeaturetabs } from './Featuretabs'
import { FeaturetabsProps } from './Featuretabs'

interface FeaturetabsBlokProps {
  blok: { tabs: any; content: any; featureCards: any } & SbBlokData
  rels?: any
}

const Featuretabs: FC<FeaturetabsBlokProps> = ({ blok }) => {
  const [renderTabs, setRenderTabs] = useState<FeaturetabsProps['tabs']>([])
  const [specialty, setSpecialty] = useState([])

  useEffect(() => {
    const fetchTabs = async () => {
      let tabs = [] as FeaturetabsProps['tabs']

      if (blok.tabs && blok.tabs.length > 0) {
        const fetchArticles = blok.tabs.map(async (tab: any) => {
          if (tab.relatedArticles && tab.relatedArticles.length > 0) {
            let location = process.env.NEXT_PUBLIC_NEXT_LOCATION
              ? process.env.NEXT_PUBLIC_NEXT_LOCATION
              : 'au'
            let filterLocation = location === 'en-nz' ? 'au' : `en-nz`
            let sbParams = {
              ...searchParamsData,
              filter_query: {
                component: { in: 'article' },
                __or: [
                  { 'settings.location_available': { in: location } },
                  { 'settings.location_available': { not_in: filterLocation } },
                ],
              },
              by_uuids: tab.relatedArticles.join(','),
              resolve_relations: 'article.author',
              sort_by: 'created_at:asc',
              per_page: 3,
            }

            const storyblokApi = getStoryblokApi()
            let { data } = await storyblokApi.get(`cdn/stories/`, sbParams)
            let articles = []
            if (data.stories && data.stories.length > 0) {
              for (let i = 0; i < data.stories.length; i++) {
                let article = data.stories[i]

                let author = null
                if (article.content.author) {
                  author = (
                    <PhnxAuthorcard
                      name={article.content.author.name}
                      layout="compact"
                      asset={{
                        src: article.content.author?.content?.image?.filename,
                        alt: article.content.author?.content?.image?.alt,
                      }}
                    />
                  )
                }

                let asset = article.content?.card_asset || null
                if (article.content.featured_image) {
                  asset = article.content.featured_image
                }
                let link = null
                if (article.full_slug) {
                  link = '/' + article.full_slug
                }
                let title = article.content.title
                let articleContent = {
                  title: title,
                  place: article.content.place,
                  asset: { src: asset?.filename, alt: asset?.alt, focus: asset?.focus },
                  tags: article.content.tags,
                  link: link,
                  author: author,
                }
                articles.push(articleContent)
              }
            }

            return articles
          }
          return []
        })

        const allArticles = await Promise.all(fetchArticles)

        blok.tabs.forEach((tab: any, index: number) => {
          tabs.push({
            id: tab?._uid,
            title: tab?.title,
            specialists: tab?.specialists.map((specialist: any) => ({
              icon: specialist?.icon,
              asset: specialist?.asset?.filename,
              label: specialist?.label,
              href: specialist?.link?.cached_url,
            })),
            solutions: tab?.solutions.map((solution: any) => ({
              icon: solution?.icon,
              asset: solution?.asset?.filename,
              label: solution?.label,
              href: solution?.link?.cached_url,
            })),
            description: {
              title: tab?.description_title,
              content: (
                <>
                  {tab?.description_content.map((nestedBlok: SbBlokData) => (
                    <StoryblokServerComponent blok={nestedBlok} key={nestedBlok._uid} />
                  ))}
                </>
              ),
            },
            featureCards: (
              <>
                {tab.featureCards &&
                  tab?.featureCards.map((featureCard: SbBlokData) => (
                    <StoryblokServerComponent blok={featureCard} key={featureCard._uid} />
                  ))}
              </>
            ),
            relatedArticles: allArticles[index],
          })
        })
      }

      setRenderTabs(tabs)
    }

    fetchTabs()
  }, [blok.tabs])

  useEffect(() => {
    const fetchSpecialty = async () => {
      let location =
        process.env.NEXT_PUBLIC_NEXT_LOCATION != null &&
        process.env.NEXT_PUBLIC_NEXT_LOCATION !== ''
          ? process.env.NEXT_PUBLIC_NEXT_LOCATION
          : 'au'
      const dimension =
        location === 'en-nz' ? 'nz' : (process.env.NEXT_PUBLIC_NEXT_LOCATION ?? 'au')

      const storyblokApi = getStoryblokApi()
      const response = await storyblokApi.get(`cdn/datasource_entries`, {
        datasource: 'service-lines',
        token: process.env.NEXT_PUBLIC_STORYBLOK_PREVIEW_TOKEN,
        dimension,
      })

      // Filter out entries with null values for the current location
      const filteredEntries = response.data.datasource_entries.filter(
        (entry: { value: string; name: string; dimension_value?: string }) => {
          return entry.dimension_value !== '' && entry.dimension_value !== 'null'
        }
      )

      setSpecialty(filteredEntries)
    }
    fetchSpecialty()
  }, [])

  return (
    <div {...storyblokEditable(blok)}>
      <PhnxFeaturetabs
        specialty={specialty}
        tabs={renderTabs}
        content={
          blok.content && (
            <div>
              {blok.content.map((nestedBlok: SbBlokData) => (
                <StoryblokServerComponent blok={nestedBlok} key={nestedBlok._uid} />
              ))}
            </div>
          )
        }
      />
    </div>
  )
}

export default Featuretabs
